import React, { useState } from 'react';
import styles from './Legend.module.css';
import Checkbox from '../Checkbox/Checkbox';
import { ChevronUpIcon, PinIcon } from '../Icon';
import { getHatchBackground } from '../../utils/legendStyles';

const LegendSection = ({
    title,
    items,
    activeFilters,
    onToggle,
    onSelectAll,
    onClearAll,
    showCount = true,
    isMarker = false
}) => {
    return (
        <div className={styles.legendSection}>
            <div className={styles.legendSectionHeader}>
                <h4 className={styles.legendSectionTitle}>
                    {title}
                    {showCount && items.length > 0 && (
                        <span className={styles.legendCount}>{activeFilters.length}/{items.length}</span>
                    )}
                </h4>
                {items.length > 0 && (
                    <div className={styles.legendSectionControls}>
                        <button className={styles.legendButton} onClick={() => onSelectAll(title.toLowerCase().replace(' ', '_'))}>Все</button>
                        <span className={styles.legendControlsSeparator}>|</span>
                        <button className={styles.legendButton} onClick={() => onClearAll(title.toLowerCase().replace(' ', '_'))}>Сброс</button>
                    </div>
                )}
            </div>
            <div className={styles.legendSectionContent}>
                {items.map(item => (
                    <div key={item.id} className={styles.legendItem} onClick={() => onToggle(item.id, title.toLowerCase().replace(' ', '_'))}>
                        <Checkbox
                            checked={activeFilters.includes(item.id)}
                            onChange={() => onToggle(item.id, title.toLowerCase().replace(' ', '_'))}
                        />
                        {isMarker ? (
                            <span className={styles.legendIcon}>
                                <img
                                    src={item.image_url}
                                    alt={item.name}
                                    crossOrigin="anonymous"
                                    onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                                    style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                                />
                                <PinIcon size={16} className={styles.markerFallback} />
                            </span>
                        ) : (
                            <span
                                className={styles.legendColorBox}
                                style={{
                                    backgroundColor: item.color === 'transparent' ? 'transparent' : (item.color || '#3b82f6'),
                                    backgroundImage: item.color === 'transparent'
                                        ? 'repeating-conic-gradient(#cbd5e1 0% 25%, transparent 0% 50%) 50% / 6px 6px'
                                        : getHatchBackground(item.hatch || 'none')
                                }}
                                title={item.hatch && item.hatch !== 'none' ? `Штриховка: ${item.hatch}` : undefined}
                            />
                        )}
                        <span className={styles.legendLabel}>{item.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const Legend = ({
    plotLegendData = [],
    buildingLegendData = [],
    roadLegendData = [],
    markerLegendData = [],
    activePlotFilters = [],
    activeBuildingFilters = [],
    activeRoadFilters = [],
    activeMarkerFilters = [],
    onToggleCategory,
    onSelectAll,
    onClearAll,
    showStatusIcons = true,
    activeStatusFilters = [],
    onToggleShowStatusIcons,
    onToggleStatusFilter,
    floating = false,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(floating);

    // Проверяем, есть ли данные для отображения
    const hasData = plotLegendData.length > 0 || buildingLegendData.length > 0 ||
                    roadLegendData.length > 0 || markerLegendData.length > 0;

    return (
        <div className={`${styles.legendContainer} ${isCollapsed ? styles.collapsed : ''} ${floating ? styles.floating : ''}`}>
            <div className={styles.legendHeader} onClick={() => setIsCollapsed(!isCollapsed)}>
                <h3 className={styles.legendTitle}>Легенда карты</h3>
                <button className={styles.toggleButton} type="button" aria-label="Свернуть/Развернуть">
                    <ChevronUpIcon size={18} className={!isCollapsed ? styles.iconDown : ''} />
                </button>
            </div>

            {!isCollapsed && hasData && (
                <div className={styles.legendContent}>
                    {/* Секция статусов */}
                    <div className={styles.legendSection}>
                        <div className={styles.legendSectionHeader}>
                            <h4 className={styles.legendSectionTitle}>
                                Обозначения статусов
                                <span className={styles.legendCount}>
                                    {activeStatusFilters.length}/4
                                </span>
                            </h4>
                            <div className={styles.legendSectionControls}>
                                <Checkbox
                                    checked={showStatusIcons}
                                    onChange={onToggleShowStatusIcons}
                                />
                            </div>
                        </div>
                        <div className={styles.legendSectionContent}>
                            <div className={styles.legendItem} onClick={(e) => { e.preventDefault(); onToggleStatusFilter('for_sale'); }}>
                                <Checkbox checked={activeStatusFilters.includes('for_sale')} readOnly />
                                <span className={styles.legendColorBox} style={{ backgroundColor: '#ef4444' }}></span>
                                <span className={styles.legendLabel}>В продаже</span>
                            </div>
                            <div className={styles.legendItem} onClick={(e) => { e.preventDefault(); onToggleStatusFilter('is_building'); }}>
                                <Checkbox checked={activeStatusFilters.includes('is_building')} readOnly />
                                <span className={styles.legendColorBox} style={{ backgroundColor: '#f59e0b' }}></span>
                                <span className={styles.legendLabel}>Строится</span>
                            </div>
                            <div className={styles.legendItem} onClick={(e) => { e.preventDefault(); onToggleStatusFilter('is_planned'); }}>
                                <Checkbox checked={activeStatusFilters.includes('is_planned')} readOnly />
                                <span className={styles.legendColorBox} style={{ backgroundColor: '#3b82f6' }}></span>
                                <span className={styles.legendLabel}>В планах</span>
                            </div>
                            <div className={styles.legendItem} onClick={(e) => { e.preventDefault(); onToggleStatusFilter('no_status'); }}>
                                <Checkbox checked={activeStatusFilters.includes('no_status')} readOnly />
                                <span className={styles.legendColorBox} style={{ backgroundColor: '#94a3b8' }}></span>
                                <span className={styles.legendLabel}>Без статуса</span>
                            </div>
                        </div>
                    </div>

                    {/* Секция участков */}
                    {plotLegendData.length > 0 && (
                        <LegendSection
                            title="Участки"
                            items={plotLegendData}
                            activeFilters={activePlotFilters}
                            onToggle={onToggleCategory}
                            onSelectAll={onSelectAll}
                            onClearAll={onClearAll}
                        />
                    )}

                    {/* Секция зданий */}
                    {buildingLegendData.length > 0 && (
                        <LegendSection
                            title="Здания"
                            items={buildingLegendData}
                            activeFilters={activeBuildingFilters}
                            onToggle={onToggleCategory}
                            onSelectAll={onSelectAll}
                            onClearAll={onClearAll}
                        />
                    )}

                    {/* Секция дорог */}
                    {roadLegendData.length > 0 && (
                        <div className={styles.legendSection}>
                            <div className={styles.legendSectionHeader}>
                                <h4 className={styles.legendSectionTitle}>
                                    Линии
                                    <span className={styles.legendCount}>
                                        {activeRoadFilters.length}/{roadLegendData.length}
                                    </span>
                                </h4>
                                <div className={styles.legendSectionControls}>
                                    <button className={styles.legendButton} onClick={() => onSelectAll('roads')}>Все</button>
                                    <span className={styles.legendControlsSeparator}>|</span>
                                    <button className={styles.legendButton} onClick={() => onClearAll('roads')}>Сброс</button>
                                </div>
                            </div>
                            <div className={styles.legendSectionContent}>
                                {roadLegendData.map(item => (
                                    <div key={item.id} className={styles.legendItem} onClick={() => onToggleCategory(item.id, 'roads')}>
                                        <Checkbox
                                            checked={activeRoadFilters.includes(item.id)}
                                            onChange={() => onToggleCategory(item.id, 'roads')}
                                        />
                                        <span
                                            className={styles.legendLineBox}
                                            style={{
                                                borderColor: item.color || '#3b82f6',
                                                borderStyle: item.lineType === 'dashed' ? 'dashed' : item.lineType === 'dotted' ? 'dotted' : 'solid'
                                            }}
                                        />
                                        <span className={styles.legendLabel}>{item.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Секция меток */}
                    {markerLegendData.length > 0 && (
                        <LegendSection
                            title="Метки"
                            items={markerLegendData}
                            activeFilters={activeMarkerFilters}
                            onToggle={onToggleCategory}
                            onSelectAll={onSelectAll}
                            onClearAll={onClearAll}
                            isMarker={true}
                        />
                    )}
                </div>
            )}

            {/* Индикатор, что данных нет */}
            {!hasData && (
                <div className={styles.legendLoading}>
                    <div className={styles.spinner}></div>
                    <div>Загрузка легенды...</div>
                </div>
            )}
        </div>
    );
};

export default Legend;
